// Wraps a rendered chart so it can leave the app as a picture.
//
// ⚠️ NO RADIX MENU HERE, AND THAT IS THE FIX. The format picker was a
// DropdownMenu, and tapping share scrolled the page to the chart and then froze
// it. Radix menus portal their content, trap focus and — modal by default — lock
// body scroll; opening a native share sheet from inside one takes focus away
// before that cleanup runs. Setting modal={false} and preventing focus return
// did not settle it, and it cannot be reproduced without a device.
//
// So the mechanism is gone rather than tuned. Two buttons, plain component
// state, rendered in normal flow: nothing portals, nothing traps focus, nothing
// touches body overflow, so there is no lock left to strand. A picker this small
// never needed a menu primitive.
//
// ⚠️ THE BUTTON ONLY EXISTS WHERE A FILE CAN BE SHARED. Desktop browsers mostly
// cannot put a file in a share sheet, and offering a control there that quietly
// downloads instead is a different feature wearing the same label.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ImageDown, Loader2, X } from "lucide-react";
import { CARD_FORMATS, canShareFiles, shareChart, type CardFormat } from "@/lib/shareChart";
import type { CardSeries } from "@/lib/cardChart";
import { cn } from "@/lib/utils";

export function ShareableChart({
  title,
  source,
  headline,
  headlineDelta,
  headlineDir,
  series,
  bars,
  formatValue,
  children,
}: {
  title: string;
  /** "USDA LM_XB403 · 2026-09-02" — carried into the picture. See shareChart. */
  source?: string;
  /** The latest print and its move. Absent when the series cannot name one. */
  headline?: string;
  headlineDelta?: string;
  headlineDir?: "up" | "down" | "flat";
  /**
   * The series itself. When given, the card DRAWS the chart at card size
   * instead of photographing the one on screen — which is what lets it fill the
   * card, and what keeps a reader's crosshair out of the picture. Absent for
   * chart shapes the card cannot draw honestly; those are still photographed.
   */
  series?: CardSeries[];
  bars?: boolean;
  formatValue?: (v: number) => string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => setEnabled(canShareFiles()), []);

  const onShare = async (format: CardFormat) => {
    if (!ref.current || busy) return;
    setOpen(false);
    setBusy(true);
    try {
      await shareChart(
        ref.current,
        { title, source, headline, headlineDelta, headlineDir, formatValue },
        format,
        series,
        bars,
      );
    } catch {
      // shareChart already falls back to a download; anything reaching here is a
      // browser that cannot rasterise at all, and a toast over a chart the reader
      // is studying costs more than the failed share.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="group relative my-2">
      {children}

      {enabled ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={busy}
            aria-label={open ? "Close share options" : "Share this chart as a picture"}
            aria-expanded={open}
            className={cn(
              "absolute right-1 top-1 z-10 rounded-md bg-background/85 p-1.5 text-muted-foreground backdrop-blur-sm",
              "transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100",
              open || busy ? "opacity-100" : "md:opacity-0",
            )}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : open ? (
              <X className="h-3.5 w-3.5" />
            ) : (
              <ImageDown className="h-3.5 w-3.5" />
            )}
          </button>

          {/* ⚠️ IN NORMAL FLOW, NOT A PORTAL. Positioned against the chart's own
              box so it moves with the page and cannot leave anything behind on
              body when it closes. */}
          {open ? (
            <div className="absolute right-1 top-10 z-10 w-52 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
              {(Object.keys(CARD_FORMATS) as CardFormat[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => void onShare(f)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-accent"
                >
                  <span className="text-[13px] text-foreground">{CARD_FORMATS[f].label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {CARD_FORMATS[f].w} × {CARD_FORMATS[f].h}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
